/**
 * What a sealed camera credential actually contains.
 *
 * The sealed box carries an opaque string; this decides what goes inside it, in
 * one place, so the browser that seals and the agent that opens cannot drift into
 * disagreeing about the format.
 *
 * JSON rather than "user:pass". Camera passwords contain colons, and a
 * concatenated pair would silently split at the wrong one — which is not a parse
 * error, it is a login that fails for a reason nobody can see from either end.
 */
import { openSealed, sealToDevice } from './sealed-box.js';
/** Format marker inside the envelope, so the contents can change independently. */
const CREDENTIAL_VERSION = 1;
/**
 * Seals a camera credential to a device.
 *
 * The plaintext exists in the browser for the duration of this call and is never
 * sent anywhere — the caller hands the result to the cloud, which is a courier.
 */
export async function sealCameraCredential(devicePublicKey, credential) {
    if (typeof credential?.username !== 'string' || credential.username === '') {
        throw new TypeError('a camera credential needs a username');
    }
    if (typeof credential.password !== 'string' || credential.password === '') {
        /*
         * An empty password is refused rather than sealed.
         *
         * Some cameras ship with a blank password, and the right answer to that is to
         * set one, not to record its absence in an encrypted blob. Sealing it would
         * also produce an RTSP URI that fails in a confusing way at the far end.
         */
        throw new TypeError('a camera credential needs a password');
    }
    const envelope = {
        v: CREDENTIAL_VERSION,
        u: credential.username,
        p: credential.password,
    };
    return sealToDevice(devicePublicKey, JSON.stringify(envelope));
}
/**
 * Opens a sealed camera credential. Device side only.
 *
 * Throws on anything wrong. A credential that will not open must not degrade into
 * a partial one: half a credential in an RTSP URI is an authentication failure
 * that looks like a broken camera.
 */
export async function openCameraCredential(devicePrivateKey, sealed) {
    const plaintext = await openSealed(devicePrivateKey, sealed);
    let envelope;
    try {
        envelope = JSON.parse(plaintext);
    }
    catch {
        throw new TypeError('sealed credential is not valid JSON');
    }
    const { v, u, p } = (envelope ?? {});
    if (v !== CREDENTIAL_VERSION) {
        throw new TypeError(`unsupported credential version ${JSON.stringify(v)}`);
    }
    if (typeof u !== 'string' || typeof p !== 'string' || u === '' || p === '') {
        throw new TypeError('sealed credential is missing a username or password');
    }
    return { username: u, password: p };
}
//# sourceMappingURL=camera-credential.js.map